import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { ApiError } from '../../api/client';
import type { Session } from '../../api/session';
import {
  closePass, createPass, createPassRequest, createTemplate, getObject, getObjectAddresses, getPass, getPasses, getTemplates, getVehicleModels,
  type NewPass, type ObjectInfo,
} from '../../api/tenant';
import { passOptions, type GuestKind, type Mode } from '../../domain/availability';
import { passPageLink, passReadyMessage } from '../../domain/invite';
import { formatServerDate, startOfMoscowDay } from '../../domain/moscowTime';
import { describePassError, type PassErrorView } from '../../domain/passErrors';
import { GuestType } from '../../domain/passes';
import { normalizePlate } from '../../domain/plate';
import { extendedUntil } from '../../domain/crew';
import { appInviteMessage } from '../../domain/deepLink';
import { editPrefill } from '../../domain/passEdit';
import { passWindow, type PassDuration, type When } from '../../domain/schedule';
import type { TelegramApi } from '../../telegram/webapp';
import { Chips, Segmented } from '../../ui/Choice';
import { Skeleton } from '../../ui/Skeleton';
import { LoadFailed } from '../common/LoadFailed';
import { MessageScreen } from '../auth/screens';
import { UntilField } from '../common/UntilField';
import { DURATION_LABEL, windowCaption } from '../common/windowCaption';
import s from './passForm.module.css';

interface Props {
  session: Session;
  telegram: TelegramApi;
  object: ObjectInfo;
}

const DURATION_CODE: Record<PassDuration, number> = { oneTime: 1, temporary: 2, permanent: 3 };
const WHEN_CHIPS: Array<{ id: 'now' | 'today' | 'tomorrow' | 'date'; label: string }> = [
  { id: 'now', label: 'Сейчас' },
  { id: 'today', label: 'Сегодня' },
  { id: 'tomorrow', label: 'Завтра' },
  { id: 'date', label: 'Дата…' },
];
const NO_ERRORS: PassErrorView = { general: null, fields: {} };
/** Visit purposes visible before «Ещё N»: the first ones the object lists. */
const PURPOSES_SHOWN = 5;

/** Sheet «Новый пропуск» (spec §6.3): one screen, a field appears only when the object needs it. */
export function NewPassScreen({ session, telegram, object }: Props) {
  const [params] = useSearchParams();
  const guestParam = params.get('guest');
  /** «Изменить пропуск»: the pass this order replaces (roadmap P0-4). */
  const editId = Number(params.get('edit')) || null;
  /** «Продлить на неделю» from the card: the same replace flow with the last day moved forward. */
  const extend = params.get('extend') !== null;
  /** «Мои машины» → «Добавить машину»: a permanent car pass. */
  const permanentCar = params.get('duration') === 'permanent';
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const addresses = useQuery({ queryKey: ['addresses', object.id], queryFn: () => getObjectAddresses(session, object.id) });
  const details = useQuery({ queryKey: ['object', object.id], queryFn: () => getObject(session, object.id) });
  const templates = useQuery({ queryKey: ['templates'], queryFn: () => getTemplates(session), enabled: !!guestParam?.startsWith('template:') });
  const requireModel = details.data?.settings.passesRequireModel === true;
  const models = useQuery({ queryKey: ['vehicle-models'], queryFn: () => getVehicleModels(session), enabled: requireModel, staleTime: Infinity });
  // Recent passes are already cached by the home screen; used for "repeat a guest" prefill only.
  useQuery({ queryKey: ['passes', 'recent'], queryFn: () => getPasses(session), enabled: !!guestParam && !guestParam.startsWith('template:') });

  const opts = useMemo(() => passOptions(details.data?.settings ?? {}), [details.data]);
  const usable = useMemo(() => (addresses.data ?? []).filter((a) => !a.isBlocked), [addresses.data]);

  const [addressId, setAddressId] = useState<number | null>(null);
  const [guest, setGuest] = useState<GuestKind | null>(permanentCar ? 'vehicle' : null);
  const [plate, setPlate] = useState('');
  const [name, setName] = useState('');
  const [modelText, setModelText] = useState('');
  const [vehicleTypeId, setVehicleTypeId] = useState<number | null>(null);
  const [duration, setDuration] = useState<PassDuration>(permanentCar ? 'permanent' : 'oneTime');
  const [whenId, setWhenId] = useState<'now' | 'today' | 'tomorrow' | 'date'>('now');
  const [date, setDate] = useState('');
  const [until, setUntil] = useState('');
  const [purposeId, setPurposeId] = useState<number | null>(null);
  const [comment, setComment] = useState('');
  const [commentOpen, setCommentOpen] = useState(false);
  const [remember, setRemember] = useState(false);
  const [forceRequest, setForceRequest] = useState(false);
  const [errors, setErrors] = useState<PassErrorView>(NO_ERRORS);
  const [done, setDone] = useState<{ mode: Mode; passId: number | null; oldClosed?: boolean } | null>(null);
  const editing = useQuery({ queryKey: ['pass', editId], queryFn: () => getPass(session, editId!), enabled: editId !== null });
  const prefilled = useRef(false);
  const fromTemplate = !!guestParam?.startsWith('template:');

  // Defaults once the object is known: first usable address, first allowed guest kind.
  useEffect(() => {
    if (addressId === null && usable.length) setAddressId(usable[0].id);
  }, [usable, addressId]);
  useEffect(() => {
    if (guest === null && opts.guestTypes.length) setGuest(opts.guestTypes[0]);
  }, [opts, guest]);

  // Prefill from a frequent guest: template:<id>, vehicle:<plate> or pedestrian:<name>.
  useEffect(() => {
    if (prefilled.current || !guestParam) return;
    const [kind, ...rest] = guestParam.split(':');
    const value = rest.join(':');
    if (kind === 'template') {
      const t = templates.data?.find((x) => String(x.id) === value);
      if (!t) return;
      prefilled.current = true;
      setAddressId(t.address.id);
      if (t.guestType === GuestType.Pedestrian) {
        setGuest('pedestrian');
        setName(t.guestData?.name ?? '');
      } else {
        setGuest('vehicle');
        setPlate(normalizePlate(t.guestData?.plateNumber ?? ''));
      }
    } else if (kind === 'vehicle' || kind === 'pedestrian') {
      prefilled.current = true;
      setGuest(kind);
      if (kind === 'vehicle') setPlate(normalizePlate(value));
      else setName(value);
    }
  }, [guestParam, templates.data]);

  useEffect(() => {
    if (prefilled.current || !editing.data) return;
    prefilled.current = true;
    const v = editPrefill(editing.data, new Date());
    setAddressId(v.addressId);
    setGuest(v.guest);
    setPlate(normalizePlate(v.plate));
    setName(v.name);
    setDuration(v.duration);
    setWhenId(v.whenId);
    setDate(v.date);
    setUntil(extend && v.duration === 'temporary' ? extendedUntil(v.until, new Date()) : v.until);
    setComment(v.comment);
    setCommentOpen(!!v.comment);
  }, [editing.data]);

  const durationItems = useMemo(() => {
    const list: Array<{ id: PassDuration; mode: Mode }> = guest ? opts.durations(guest).map((d) => ({ id: d.duration, mode: d.mode })) : [];
    if (opts.permanent) list.push({ id: 'permanent', mode: opts.permanent });
    return list;
  }, [opts, guest]);
  useEffect(() => {
    // Until the guest kind is known the list holds only «Постоянный»; picking it then made it the default.
    if (guest === null && opts.guestTypes.length) return;
    if (durationItems.length && !durationItems.some((d) => d.id === duration)) setDuration(durationItems[0].id);
  }, [durationItems, duration, guest, opts]);

  const mode: Mode = forceRequest ? 'request' : durationItems.find((d) => d.id === duration)?.mode ?? 'direct';
  const when: When = whenId === 'date' ? (date ? { date } : 'today') : whenId;
  const limitHours = (() => {
    const v = details.data?.settings[duration === 'temporary' ? 'passesLimitHoursTemporary' : 'passesLimitHoursOneTime'];
    return typeof v === 'number' && v > 0 ? v : null;
  })();
  const now = new Date();
  const window = passWindow({ when, duration, limitHours, now, forRequest: mode === 'request', until });
  const vehicleTypes = details.data?.vehicleTypes ?? [];
  const purposes = details.data?.options ?? [];

  const submit = useMutation({
    mutationFn: async () => {
      const model = models.data?.find((m) => m.title === modelText);
      const body: NewPass = {
        addressId: addressId!,
        durationType: DURATION_CODE[duration],
        guestType: guest === 'pedestrian' ? GuestType.Pedestrian : GuestType.Vehicle,
        guestData:
          guest === 'pedestrian'
            ? { name: name.trim() }
            : { plateNumber: plate, ...(model ? { modelId: model.id } : {}), ...(vehicleTypeId ? { vehicleType: vehicleTypeId } : {}) },
        ...(window.startsAt ? { startsAt: formatServerDate(window.startsAt) } : {}),
        ...(window.expiresAt ? { expiresAt: formatServerDate(window.expiresAt) } : {}),
        ...(comment.trim() ? { comment: comment.trim() } : {}),
        ...(purposeId ? { options: [purposeId] } : {}),
      };
      let passId: number | null = null;
      if (mode === 'request') await createPassRequest(session, body);
      else passId = (await createPass(session, body))?.id ?? null;
      // The replaced pass is closed only once the new one exists; a request still waits for the УК,
      // so the old pass keeps working until then.
      let oldClosed: boolean | undefined;
      if (editId !== null && mode !== 'request') {
        oldClosed = await closePass(session, editId).then(() => true, () => false);
      }
      if (remember && !fromTemplate) {
        // Best effort: the pass is already ordered, a failed template must not look like a failed order.
        void createTemplate(session, {
          name: guest === 'pedestrian' ? name.trim() : plate,
          addressId: body.addressId,
          durationType: body.durationType,
          guestType: body.guestType,
          guestData: body.guestData,
        })
          .then(() => queryClient.invalidateQueries({ queryKey: ['templates'] }))
          .catch(() => undefined);
      }
      return { mode, passId, oldClosed };
    },
    onSuccess: (result) => {
      telegram.notify('success');
      void queryClient.invalidateQueries({ queryKey: ['passes'] });
      setDone(result);
    },
    onError: (error) => {
      telegram.notify('error');
      if (error instanceof ApiError && error.code === 'PASS_TYPE_ALLOWED_BY_REQUEST_ONLY') {
        setForceRequest(true);
        setErrors({ general: 'Этот пропуск на объекте оформляется через согласование с УК', fields: {} });
        return;
      }
      setErrors(describePassError(error));
    },
  });

  // The created pass carries no sharedToken in every response, so the guest page link is read from the pass itself.
  const share = useMutation({
    mutationFn: (passId: number) => getPass(session, passId),
    onSuccess: (pass) => {
      const link = passPageLink(pass.sharedToken);
      if (link) telegram.share(link, passReadyMessage(object.name));
      else void navigate(`/passes/${pass.id}`);
    },
    onError: (_e, passId) => void navigate(`/passes/${passId}`),
  });

  const onSubmit = () => {
    const fields: PassErrorView['fields'] = {};
    if (guest === 'vehicle' && plate.length < 3) fields.plate = 'Введите госномер';
    if (guest === 'pedestrian' && !name.trim()) fields.name = 'Введите имя гостя';
    if (guest === 'vehicle' && requireModel && !models.data?.some((m) => m.title === modelText)) fields.model = 'Выберите модель из списка';
    if (guest === 'vehicle' && vehicleTypes.length && !vehicleTypeId) fields.vehicleType = 'Выберите тип транспорта';
    setErrors({ general: whenId === 'date' && !date ? 'Выберите дату визита' : null, fields });
    if (Object.keys(fields).length || (whenId === 'date' && !date)) return;
    submit.mutate();
  };

  if (done) {
    const { passId } = done;
    return (
      <main className={s.done}>
        <h1 className={s.doneTitle}>{done.mode === 'request' ? 'Заявка отправлена' : editId ? 'Пропуск изменён' : 'Пропуск оформлен'}</h1>
        <p className={s.doneText}>
          {done.mode === 'request'
            ? `УК рассмотрит заявку; статус — в разделе «Обращения».${editId ? ' Прежний пропуск действует, пока заявку не согласуют.' : ''}`
            : 'Охрана уже видит пропуск. Гость проедет по номеру или имени.'}
          {done.oldClosed === false && ' Прежний пропуск закрыть не удалось — закройте его в карточке.'}
        </p>
        {passId !== null && (
          <button type="button" className={s.submit} disabled={share.isPending} onClick={() => share.mutate(passId)}>
            {share.isPending ? 'Готовим ссылку…' : 'Отправить гостю'}
          </button>
        )}
        <Link to="/" className={passId !== null ? s.secondary : s.submit}>
          На главную
        </Link>
        {!editId && (
          <button
            type="button"
            className={s.more}
            onClick={() => telegram.share(telegram.appLink(`obj_${object.id}`), appInviteMessage(object.name, telegram.name))}
          >
            Пусть семья тоже заказывает пропуска — пригласить
          </button>
        )}
      </main>
    );
  }

  if (addresses.isError || details.isError) {
    return <LoadFailed error={addresses.error ?? details.error} onRetry={() => { void addresses.refetch(); void details.refetch(); }} fullScreen />;
  }
  if (!addresses.data || !details.data) {
    return (
      <main className={s.sheet} aria-busy="true">
        <Skeleton height={44} />
        <Skeleton height={66} />
        <Skeleton height={56} />
      </main>
    );
  }
  if (usable.length === 0) {
    return <MessageScreen title="Заказ недоступен" text="Адрес заблокирован управляющей компанией. Позвоните в УК, чтобы узнать причину." />;
  }
  if (!opts.guestTypes.length && !opts.permanent) {
    return <MessageScreen title="Заказ недоступен" text="На этом объекте жители не оформляют пропуска сами. Обратитесь в УК." />;
  }

  const address = addresses.data.find((a) => a.id === addressId);

  return (
    <main className={s.sheet} aria-labelledby="new-pass-title">
      <header className={s.head}>
        <h1 id="new-pass-title" className={s.title}>
          {editId ? 'Изменить пропуск' : 'Новый пропуск'}
        </h1>
        {usable.length === 1 && address && <p className={s.where}>{address.name}</p>}
        {!editId && guest === 'vehicle' && opts.durations('vehicle').length > 0 && (
          // A plate already typed goes along into the first line of the crew (#4216).
          <Link to={plate ? `/crew?plate=${encodeURIComponent(plate)}` : '/crew'} className={s.headLink}>
            Несколько машин или стройка? Пропуск бригаде →
          </Link>
        )}
        {!editId && guest === 'pedestrian' && opts.durations('pedestrian').length > 0 && (
          <Link to="/crew?kind=pedestrian" className={s.headLink}>
            Несколько человек? Пропуск бригаде →
          </Link>
        )}
      </header>

      {addresses.data.length > 1 && (
        <Chips
          label="Адрес"
          value={addressId}
          items={addresses.data.map((a) => ({ id: a.id, label: a.isBlocked ? `${a.name} — заблокирован` : a.name, disabled: a.isBlocked }))}
          onChange={(v) => v !== null && setAddressId(v)}
        />
      )}

      {opts.guestTypes.length > 1 && guest && (
        <Segmented
          label="Кто приедет"
          value={guest}
          items={opts.guestTypes.map((g) => ({ id: g, label: g === 'vehicle' ? 'На машине' : 'Пешком' }))}
          onChange={(g) => {
            setGuest(g);
            setForceRequest(false);
          }}
        />
      )}

      {guest === 'vehicle' ? (
        <div className={s.field}>
          <label htmlFor="plate" className={s.label}>
            Госномер
          </label>
          <input
            id="plate"
            className={errors.fields.plate ? `${s.plate} ${s.invalid}` : s.plate}
            value={plate}
            onChange={(e) => setPlate(normalizePlate(e.target.value))}
            placeholder="А000АА77"
            autoComplete="off"
            autoCapitalize="characters"
            autoFocus={!guestParam}
            aria-invalid={!!errors.fields.plate}
            aria-describedby="plate-hint"
          />
          <span id="plate-hint" className={errors.fields.plate ? s.fieldError : s.hint}>
            {errors.fields.plate ?? 'Можно печатать латиницей — заменим на кириллицу'}
          </span>
        </div>
      ) : (
        <div className={s.field}>
          <label htmlFor="guest-name" className={s.label}>
            Имя гостя
          </label>
          <input
            id="guest-name"
            className={errors.fields.name ? `${s.input} ${s.invalid}` : s.input}
            value={name}
            maxLength={255}
            onChange={(e) => setName(e.target.value)}
            placeholder="Например, Анна Петровна"
            autoComplete="off"
            aria-invalid={!!errors.fields.name}
          />
          {errors.fields.name && <span className={s.fieldError}>{errors.fields.name}</span>}
        </div>
      )}

      {guest === 'vehicle' && requireModel && (
        <div className={s.field}>
          <label htmlFor="model" className={s.label}>
            Модель
          </label>
          <input id="model" list="models" className={s.input} value={modelText} onChange={(e) => setModelText(e.target.value)} placeholder="Начните вводить марку" autoComplete="off" />
          <datalist id="models">
            {(models.data ?? []).map((m) => (
              <option key={m.id} value={m.title} />
            ))}
          </datalist>
          {errors.fields.model && <span className={s.fieldError}>{errors.fields.model}</span>}
        </div>
      )}

      {guest === 'vehicle' && vehicleTypes.length > 0 && (
        <div className={s.field}>
          <span className={s.label}>Тип транспорта</span>
          <Chips label="Тип транспорта" value={vehicleTypeId} items={vehicleTypes.map((t) => ({ id: t.id, label: t.name }))} onChange={setVehicleTypeId} />
          {errors.fields.vehicleType && <span className={s.fieldError}>{errors.fields.vehicleType}</span>}
        </div>
      )}

      {durationItems.length > 1 && (
        <Segmented label="Срок пропуска" value={duration} items={durationItems.map((d) => ({ id: d.id, label: DURATION_LABEL[d.id] }))} onChange={setDuration} />
      )}

      {duration !== 'permanent' && (
        <div className={s.field}>
          <span className={s.label}>Когда</span>
          <Chips label="Когда" value={whenId} items={WHEN_CHIPS} onChange={(v) => v && setWhenId(v)} />
          {whenId === 'date' && (
            <input
              type="date"
              aria-label="Дата визита"
              className={s.input}
              value={date}
              min={formatServerDate(startOfMoscowDay(now)).slice(0, 10)}
              onChange={(e) => setDate(e.target.value)}
            />
          )}
        </div>
      )}
      {duration === 'temporary' && (
        <UntilField value={until} onChange={setUntil} startsAt={window.startsAt} expiresAt={window.expiresAt} limitHours={limitHours} />
      )}
      <p className={s.caption}>{windowCaption(duration, window.startsAt, window.expiresAt, now)}</p>

      {purposes.length > 0 && (
        <div className={s.field}>
          <span className={s.label}>Цель визита · необязательно</span>
          <Chips label="Цель визита" value={purposeId} items={purposes.map((p) => ({ id: p.id, label: p.name }))} onChange={setPurposeId} allowNone limit={PURPOSES_SHOWN} />
        </div>
      )}

      {commentOpen ? (
        <div className={s.field}>
          <label htmlFor="comment" className={s.label}>
            Комментарий для охраны
          </label>
          <textarea id="comment" className={s.textarea} value={comment} onChange={(e) => setComment(e.target.value)} rows={2} />
        </div>
      ) : (
        <button type="button" className={s.more} onClick={() => setCommentOpen(true)}>
          Комментарий для охраны
        </button>
      )}

      {!fromTemplate && (
        <label className={s.check}>
          <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} />
          Запомнить как частого гостя
        </label>
      )}

      <div className={s.footer}>
        {errors.general && (
          <p className={s.error} role="alert">
            {errors.general}
          </p>
        )}
        <button type="button" className={s.submit} onClick={onSubmit} disabled={submit.isPending}>
          {submit.isPending ? 'Отправляем…' : mode === 'request' ? 'Отправить на согласование' : editId ? 'Сохранить изменения' : 'Оформить пропуск'}
        </button>
        <Link to="/invite" className={s.outline}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M10 14a4 4 0 0 0 5.7 0l3-3a4 4 0 0 0-5.7-5.7l-1 1" />
            <path d="M14 10a4 4 0 0 0-5.7 0l-3 3a4 4 0 0 0 5.7 5.7l1-1" />
          </svg>
          Не знаю номер — отправить ссылку
        </Link>
      </div>
    </main>
  );
}
